"use client";

import { useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import type { DetailPricingData } from "@/lib/checkout-geo-pricing/useDetailPricingData";
import Icon from "@/components/icons";
import Cookies from "js-cookie";
import { LOCALE_COOKIE_NAME } from "@/lib/locale/constants";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import useIsMobile from "@/hooks/useIsMobile";
import resolveCountryName from "./utils/resolveCountryName";
import { resolvedGeoPricingText } from "./utils/resolvedGeoPricingText";
import { cn } from "@/lib/utils";
import { BadgeIcon } from "@/components/ui/badgeIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import type { GeoPricingLocation } from "./types";
import useTranslations from "@/i18n/useTranslations";

type HeroAccentCalloutProps = {
  variant?: "dark" | "light";
  pricingData?: Partial<DetailPricingData> | null;
  className?: string;
  location: GeoPricingLocation;
};

export default function HeroAccentCallout({
  variant,
  pricingData,
  className,
  location,
}: HeroAccentCalloutProps) {
  const t = useTranslations("GeoPricing");

  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const isMobile = useIsMobile();
  const isEnabled = pricingData?.geoPricing !== undefined;
  const locale = Cookies.get(LOCALE_COOKIE_NAME) ?? "en";
  const checkoutGeoPricing = pricingData?.checkoutGeoPricingEligibility;
  const isEligible = Boolean(
    isEnabled && pricingData?.checkoutGeoPricingEligibility?.eligible,
  );

  const countryCode = checkoutGeoPricing?.countryCode ?? "";
  const countryName = resolveCountryName(countryCode, locale);
  const percentageDiscount = checkoutGeoPricing?.discountPercentage ?? 0;
  const heroAccentCallout = pricingData?.geoPricing?.heroAccentCallout;
  const resolvedBadgeText = isEligible
    ? resolvedGeoPricingText(heroAccentCallout?.badgeText, {
        percentageDiscount,
        countryName,
      })
    : "";
  const resolvedText = isEligible
    ? resolvedGeoPricingText(heroAccentCallout?.text, {
        percentageDiscount,
        countryName,
      })
    : "";
  const badgeIcon = "star-filled";
  const resolvedTooltipText = resolvedGeoPricingText(
    heroAccentCallout?.tooltipText,
    { percentageDiscount, countryName },
  );

  const badgeWebElement = useMemo(
    () => ({
      elementType: "TEXT",
      htmlId: "geo-discount-badge",
      location,
      name: "geo-badge",
      text: resolvedBadgeText,
    }),
    [location, resolvedBadgeText],
  );

  const { elementViewed } = useElementEvents({ webElement: badgeWebElement });

  useEffect(() => {
    if (inView && resolvedBadgeText && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed, resolvedBadgeText]);

  if (!isEligible) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col md:flex-row gap-4 items-start md:items-center justify-start",
        className,
      )}
    >
      {resolvedBadgeText && (
        <span ref={inViewRef}>
          <BadgeIcon
            key={badgeIcon}
            icon={badgeIcon}
            color={variant === "dark" ? "primaryDark" : "primaryLight"}
            withBorder
            iconSize={14}
            className={cn(
              "shrink-0 whitespace-nowrap gap-2",
              variant === "dark" &&
                "md:border-primary-500 md:bg-[#BEF4E5] md:text-primary-light",
              variant === "light"
                ? "md:border-primary-light md:bg-secondary-500 md:text-white"
                : "",
            )}
          >
            <span className="text-micro">{resolvedBadgeText}</span>
          </BadgeIcon>
        </span>
      )}
      <div className="flex items-center gap-1">
        <span className="text-base md:text-lg font-bold">{resolvedText}</span>
        {resolvedTooltipText ? (
          <Popover>
            <PopoverTrigger
              aria-label={t("information")}
              className="inline-flex shrink-0 items-center"
            >
              <Icon
                type="info"
                svgProps={{
                  width: 18,
                  height: 18,
                  className: "text-inherit hover:text-gray-300",
                }}
              />
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side={isMobile ? "bottom" : "right"}
              className="max-w-xs rounded-xl bg-primary text-sm text-primary-foreground"
            >
              {sanitizeAndParseInlineHTML(resolvedTooltipText, true)}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </div>
  );
}
